import React, { useState, useEffect } from 'react';
import useIsMobile from './useIsMobile';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import useUserStore from '../../stores/useUserStore';
import useChatStore from '../../stores/useChatStore';
import FlerpieChatPanel from './FlerpieChatPanel';
import { MS, usePresence } from '../ui/motion';

// Routes where Flerpie should be hidden (full-screen modes)
const HIDDEN_ROUTES = ['/office', '/scanner'];

const FlerpieChatBot: React.FC = () => {
  const user = useUserStore((s: { user: any }) => s.user);
  const isOpen = useChatStore((s: { isOpen: boolean }) => s.isOpen);
  const location = useLocation();
  const isMobile = useIsMobile();

  // Delay entrance so Flerpie arrives after the page settles
  const [ready, setReady] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setReady(true), 1500);
    return () => clearTimeout(t);
  }, []);

  // Notes waiting for me: checked on arrival and every two minutes. Closed, the
  // launcher's badge shows them. Open on the notes thread, they land there; open on
  // any other conversation, the badge on the header's sidebar toggle shows them.
  const canChat = user.loggedin && (user.can_ai || user.isadmin || user.can_admin);
  useEffect(() => {
    if (!canChat) return undefined;
    const check = () => useChatStore.getState().fetchPendingNotes().then((n: number) => {
      const { isOpen: open, activeThread } = useChatStore.getState();
      if (n > 0 && open && activeThread?.kind === 'notes') useChatStore.getState().deliverNotes();
    });
    check();
    const id = setInterval(check, 120000);
    return () => clearInterval(id);
  }, [canChat]);

  const hiddenHere = HIDDEN_ROUTES.some((route) => location.pathname.startsWith(route));
  const shown = canChat && !hiddenHere && ready;
  // The nav launcher exists wherever Flerpie does, on every width; only desktop docks.
  const docked = shown && !isMobile;
  useEffect(() => {
    useChatStore.getState().setDockReady(shown);
    return () => useChatStore.getState().setDockReady(false);
  }, [shown]);
  const panel = usePresence(docked && isOpen, MS.exit);

  if (!shown) return null;

  // Mobile: opened from the same nav launcher, full screen
  if (isMobile) {
    return isOpen ? createPortal(<FlerpieChatPanel />, document.body) : null;
  }

  // Desktop: the panel docked right, opened from the nav launcher
  return panel.mounted ? createPortal(<FlerpieChatPanel closing={!panel.open} />, document.body) : null;
};

export default FlerpieChatBot;
